import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import CookieBanner from '../../components/feature/CookieBanner';
import BackToTop from '../../components/feature/BackToTop';
import { injectSchema, getFAQSchema, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seoSchema';

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = 'Preguntas Frecuentes - Limpieza Post Incendio Barcelona | FIRETRAUMAS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Preguntas frecuentes sobre limpieza post incendio en Barcelona: costes, tiempos, seguros, proceso, seguridad. Resuelve tus dudas con FIRETRAUMAS.');
    }

    // Inject Schema.org JSON-LD
    const cleanupFAQ = injectSchema(getFAQSchema());
    const cleanupBreadcrumb = injectSchema(getBreadcrumbSchema([
      { name: 'Inicio', url: '/' },
      { name: 'Preguntas Frecuentes', url: '/faq' }
    ]));
    const cleanupWebPage = injectSchema(getWebPageSchema(
      'Preguntas Frecuentes - Limpieza Post Incendio Barcelona',
      'Preguntas frecuentes sobre limpieza post incendio en Barcelona: costes, tiempos, seguros, proceso, seguridad. Resuelve tus dudas con FIRETRAUMAS.',
      '/faq'
    ));

    return () => {
      cleanupFAQ?.();
      cleanupBreadcrumb?.();
      cleanupWebPage?.();
    };
  }, []);

  const categories = [
    { id: 'all', label: 'Todas' },
    { id: 'costes', label: 'Costes' },
    { id: 'tiempos', label: 'Tiempos' },
    { id: 'seguros', label: 'Seguros' },
    { id: 'proceso', label: 'Proceso' },
    { id: 'seguridad', label: 'Seguridad' },
    { id: 'cobertura', label: 'Cobertura' }
  ];

  const faqs = [
    {
      category: 'costes',
      question: '¿Cuánto cuesta una limpieza post incendio en Barcelona?',
      answer: 'El coste varía significativamente según múltiples factores: superficie afectada en m², gravedad del incendio (leve, moderado o grave), número de habitaciones, volumen de enseres a retirar, tipo de materiales afectados, acceso al inmueble y disponibilidad de ascensor, y nivel de urgencia. Como referencia orientativa, un piso de 70-90 m² con daños moderados puede oscilar entre 2.000€ y 5.000€, pero cada caso es único. Ofrecemos presupuestos personalizados sin compromiso tras una inspección inicial. No cobramos por la visita de evaluación en Barcelona y AMB.'
    },
    {
      category: 'costes',
      question: '¿Qué factores afectan al precio final?',
      answer: 'Los principales factores son: extensión del área afectada, profundidad de penetración del hollín en materiales porosos, necesidad de retirada de enseres y volumen de los mismos, tipo de tratamiento de olores requerido, accesibilidad del inmueble (planta, ascensor, parking para furgoneta), coordinación con perito (puede alargar plazos), y urgencia del servicio. También influye si hay elementos salvables que requieren limpieza especializada versus retirada completa. Siempre desglosamos el presupuesto para máxima transparencia.'
    },
    {
      category: 'costes',
      question: '¿Puedo pagar a plazos o financiar el servicio?',
      answer: 'Si tu seguro cubre el siniestro, normalmente no necesitas adelantar el pago completo. Trabajamos directamente con la aseguradora en muchos casos. Si pagas de forma particular, aceptamos pago fraccionado en dos partes: 50% al inicio y 50% a la entrega. Para proyectos de mayor envergadura, estudiamos opciones de financiación. Consúltanos tu caso específico y buscamos la mejor solución.'
    },
    {
      category: 'tiempos',
      question: '¿Cuánto tarda una limpieza completa?',
      answer: 'El tiempo depende de la gravedad y extensión. Como ejemplos orientativos no vinculantes: un piso de 60 m² con incendio leve en cocina puede tardar 2-3 días laborables; un piso de 90 m² con daños moderados en varias estancias, entre 4-6 días; un piso de 120 m² con daños graves y necesidad de vaciado completo, entre 7-10 días o más. Estos plazos pueden alargarse si hay que esperar aprobaciones del seguro o perito. Siempre damos una estimación realista tras la inspección inicial.'
    },
    {
      category: 'tiempos',
      question: '¿Podéis empezar el mismo día o al día siguiente?',
      answer: 'Depende de nuestra disponibilidad y de si hay seguro implicado. Para emergencias sin seguro, intentamos iniciar trabajos básicos (contención, protección) en 24-48 horas. Si hay seguro, debemos esperar la visita del perito antes de iniciar trabajos importantes, aunque podemos hacer tareas de protección previas con su autorización. En cualquier caso, respondemos a consultas en menos de 2 horas en horario laboral y evaluamos la urgencia de cada caso.'
    },
    {
      category: 'tiempos',
      question: '¿Cuándo puedo volver a casa después del incendio?',
      answer: 'Depende de la gravedad del incendio y de la calidad del aire. Tras un incendio pequeño y localizado, con buena ventilación y limpieza básica, podrías volver en 3-5 días. Tras un incendio moderado con hollín extendido, recomendamos esperar a completar limpieza, desinfección y desodorización: entre 5-10 días. Tras un incendio grave, puede ser necesario esperar 2 semanas o más. La seguridad es prioritaria. No recomendamos habitar el espacio hasta que esté completamente saneado. Te orientamos caso por caso.'
    },
    {
      category: 'seguros',
      question: '¿Cubre el seguro este servicio de limpieza?',
      answer: 'La mayoría de seguros de hogar con cobertura de incendios incluyen la limpieza post incendio dentro de los daños cubiertos. Sin embargo, cada póliza es diferente. Es fundamental que contactes con tu aseguradora inmediatamente tras el incendio, abras un parte, y esperes la visita del perito antes de iniciar trabajos importantes. Nosotros tenemos experiencia trabajando con las principales aseguradoras en Barcelona y facilitamos toda la documentación necesaria para tu reclamación.'
    },
    {
      category: 'seguros',
      question: '¿Qué documentos necesito para el seguro?',
      answer: 'Necesitarás: parte de bomberos (si intervinieron), denuncia policial (si procede), fotografías y vídeos de los daños antes de tocar nada, listado de enseres dañados con valor aproximado, presupuestos de limpieza y reparación, y facturas finales. Nosotros te ayudamos con la documentación técnica: fotografías profesionales, informes de trabajos realizados, desglose detallado de servicios, y facturas conformes. Coordinamos con el perito para facilitar el proceso.'
    },
    {
      category: 'seguros',
      question: '¿Trabajáis con todos los peritos y aseguradoras?',
      answer: 'Sí, tenemos experiencia trabajando con peritos de las principales aseguradoras que operan en Barcelona: Mapfre, Allianz, AXA, Zurich, Generali, Mutua Madrileña, Línea Directa, y muchas otras. Conocemos sus protocolos y requisitos de documentación. Esto agiliza el proceso y evita problemas en la reclamación. Si tu aseguradora no está en esta lista, no hay problema: nos adaptamos a sus requisitos.'
    },
    {
      category: 'proceso',
      question: '¿Qué hacéis en la primera visita de inspección?',
      answer: 'En la primera visita evaluamos la extensión de los daños, fotografiamos y documentamos todo, identificamos materiales afectados y su estado, evaluamos qué enseres son salvables, medimos la superficie afectada, comprobamos accesos y condiciones de trabajo, y hablamos contigo para entender tus prioridades. Con esta información preparamos un presupuesto detallado y un plan de trabajo. La visita dura entre 30-60 minutos según el caso. No cobramos por esta inspección inicial en Barcelona y AMB. Conoce más sobre nuestro proceso de trabajo en la página de servicios.'
    },
    {
      category: 'proceso',
      question: '¿Cómo limpiáis el hollín sin dañar las paredes?',
      answer: 'Utilizamos técnicas específicas según el material. Primero aspiramos con equipos HEPA para no dispersar partículas. Luego aplicamos limpiadores pH-neutro específicos para cada superficie: para paredes pintadas usamos esponjas químicas secas y productos suaves; para azulejos, limpiadores más potentes; para madera, productos específicos que no dañan el acabado. Nunca usamos agua sola (fija el hollín) ni productos abrasivos. Hacemos pruebas en zonas pequeñas antes de proceder. El objetivo es limpiar sin dañar.'
    },
    {
      category: 'proceso',
      question: '¿Qué pasa con mis muebles, ropa y objetos personales?',
      answer: 'Evaluamos cada objeto individualmente. Muebles de madera maciza suelen ser salvables con limpieza especializada. Tapicerías dependen de la penetración del humo. Ropa puede lavarse profesionalmente si el daño es leve, pero si está muy impregnada es mejor desecharla. Objetos de valor sentimental intentamos salvarlos siempre que sea seguro. Documentos y fotografías pueden tratarse con técnicas especiales. No tiramos nada sin tu aprobación y, si hay seguro, sin consultar al perito. Todo se documenta.'
    },
    {
      category: 'seguridad',
      question: '¿Es seguro entrar en casa después de un incendio?',
      answer: 'Depende de la gravedad. Tras un incendio, el aire puede contener partículas tóxicas, monóxido de carbono residual, y el hollín contiene sustancias cancerígenas. Las estructuras pueden estar debilitadas. No recomendamos entrar sin evaluación profesional. Si necesitas recuperar objetos urgentes, hazlo con mascarilla FFP3, guantes y ventilación, y el mínimo tiempo posible. Lo ideal es esperar a que profesionales evalúen la seguridad estructural y del aire. Nosotros trabajamos siempre con EPIs certificados.'
    },
    {
      category: 'seguridad',
      question: '¿Qué EPIs y protocolos de seguridad usáis?',
      answer: 'Nuestro equipo utiliza: mascarillas FFP3 o respiradores con filtros P3 para partículas, guantes nitrilo resistentes, monos desechables tipo Tyvek, gafas de protección selladas, y calzado de seguridad. Establecemos contención con plásticos para evitar dispersión de partículas a zonas no afectadas. Usamos aspiradores con filtros HEPA que capturan el 99.97% de partículas. Ventilamos de forma controlada. Seguimos protocolos de seguridad laboral y medioambiental. La seguridad de nuestro equipo y la tuya es prioritaria.'
    },
    {
      category: 'seguridad',
      question: '¿Cómo evitáis que se dispersen partículas a otras zonas?',
      answer: 'Establecemos contención física con plásticos y cintas, sellamos puertas y conductos de ventilación de zonas no afectadas, creamos presión negativa cuando es posible (aire sale pero no entra), usamos aspiradores HEPA en todo momento, humedecemos ligeramente superficies antes de manipularlas (sin empapar), y trabajamos de arriba hacia abajo y de limpio a sucio. Al finalizar cada jornada, limpiamos la zona de contención. Estos protocolos minimizan la dispersión de hollín a zonas limpias.'
    },
    {
      category: 'cobertura',
      question: '¿Atendéis fuera de Barcelona ciudad?',
      answer: 'Sí, trabajamos en toda Barcelona ciudad y el Área Metropolitana de Barcelona (AMB), que incluye más de 30 municipios: Badalona, L\'Hospitalet, Santa Coloma, Cornellà, Sant Boi, Esplugues, Sant Just, Sant Feliu, Molins de Rei, El Prat, Viladecans, Gavà, Castelldefels, Sant Adrià, Montgat, Sant Cugat, Cerdanyola, Ripollet, y muchos más. Si tu municipio no está en esta lista, consúltanos igualmente. Evaluamos cada caso y, si es viable, nos desplazamos. Llama al 690 22 09 44 para confirmar cobertura.'
    },
    {
      category: 'cobertura',
      question: '¿Trabajáis en pisos sin ascensor o con difícil acceso?',
      answer: 'Sí, trabajamos en todo tipo de inmuebles: pisos sin ascensor (incluso en plantas altas), áticos, bajos, locales comerciales, casas unifamiliares, y edificios con acceso complicado. El acceso y la planta pueden afectar al precio (por el esfuerzo de subir/bajar material y enseres) y al tiempo necesario, pero no es un impedimento. Evaluamos cada caso en la inspección inicial y ajustamos la logística. Tenemos experiencia en el casco antiguo de Barcelona y edificios históricos sin ascensor.'
    },
    {
      category: 'cobertura',
      question: '¿Qué municipios del AMB cubrís exactamente?',
      answer: 'Cubrimos regularmente: Barcelona (todos los distritos), Badalona, L\'Hospitalet de Llobregat, Santa Coloma de Gramenet, Cornellà, Sant Boi, Esplugues, Sant Just Desvern, Sant Feliu de Llobregat, Molins de Rei, El Prat, Viladecans, Gavà, Castelldefels, Sant Adrià de Besòs, Montgat, Tiana, Sant Cugat del Vallès, Cerdanyola, Ripollet, Barberà del Vallès, Montcada i Reixac, Sant Vicenç dels Horts, Pallejà, Cervelló, y otros municipios del AMB. Para municipios fuera del AMB, consúltanos.'
    },
    {
      category: 'proceso',
      question: '¿Eliminan el olor a humo por completo?',
      answer: 'En la mayoría de casos sí, pero depende de la penetración del humo en materiales porosos. Utilizamos neutralizadores profesionales, no ambientadores que enmascaran. En materiales no porosos (azulejos, vidrio, metal) eliminamos el olor completamente. En materiales porosos (madera, yeso, textiles) el éxito depende de cuánto penetró el humo. En casos extremos, puede ser necesario sellar superficies o sustituir materiales. Siempre somos realistas sobre las expectativas. Si tras nuestro tratamiento persiste olor, buscamos soluciones adicionales sin coste extra en garantía.'
    },
    {
      category: 'proceso',
      question: '¿Ofrecéis servicio de vaciado completo del piso?',
      answer: 'Sí, ofrecemos servicio de retirada y vaciado de enseres dañados, tanto parcial como completo. Incluye: clasificación de objetos (salvables/no salvables), embalaje de objetos recuperables, retirada de muebles, electrodomésticos y enseres dañados, gestión de residuos según normativa, limpieza tras el vaciado, y coordinación con perito para documentar todo. Este servicio se presupuesta aparte según volumen. Es especialmente útil cuando el seguro cubre la sustitución de contenido y prefieres empezar de cero.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-serif font-bold text-5xl md:text-6xl text-primary mb-6">
              Preguntas frecuentes sobre limpieza post incendio
            </h1>
            <p className="text-textLight text-xl mb-8">
              Resolvemos las dudas más comunes sobre nuestros servicios en Barcelona.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-textLight text-xl"></i>
              <input
                type="text"
                placeholder="Buscar tu duda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-6 py-4 rounded-full border-2 border-gray-300 focus:outline-none focus:border-secondary transition-colors text-base"
              />
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex overflow-x-auto gap-2 py-4 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-2 rounded-full font-sans font-semibold text-sm whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-secondary text-white'
                      : 'bg-bg text-textPrimary hover:bg-secondary/10'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-textLight mb-4"></i>
                <p className="text-textLight text-lg">
                  No se encontraron preguntas que coincidan con tu búsqueda.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer group"
                    >
                      <span className="font-sans font-semibold text-base md:text-lg text-primary pr-4 group-hover:text-secondary transition-colors">
                        {faq.question}
                      </span>
                      <i className={`ri-arrow-down-s-line text-2xl text-secondary transition-transform flex-shrink-0 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {openIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-textLight text-base leading-relaxed mb-4">
                          {faq.answer}
                        </p>
                        <p className="text-secondary text-sm font-semibold">
                          ¿Necesitas más detalles?{' '}
                          <a href="tel:+34690220944" className="underline hover:text-primary cursor-pointer">
                            Llámanos al 690 22 09 44
                          </a>{' '}
                          o{' '}
                          <a href="/contacto" className="underline hover:text-primary cursor-pointer">
                            solicita un presupuesto
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-bg rounded-lg p-12 text-center">
              <h2 className="font-serif font-bold text-3xl text-primary mb-4">
                ¿No encuentras tu respuesta?
              </h2>
              <p className="text-textLight text-lg mb-8">
                Estamos aquí para ayudarte. Contacta con nosotros y resolveremos tus dudas.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="tel:+34690220944"
                  className="bg-primary text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all inline-flex items-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Llamar
                </a>
                <a
                  href="https://wa.me/34690220944"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all inline-flex items-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-center text-lg text-textPrimary mb-6 font-semibold">
              Explora más información sobre nuestros servicios
            </h3>
            <div className="flex flex-wrap justify-center gap-6">
              <Link
                to="/servicios"
                className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
              >
                Ver servicios de limpieza post incendio →
              </Link>
              <Link
                to="/empresa"
                className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
              >
                Conoce nuestra empresa y valores →
              </Link>
              <Link
                to="/contacto"
                className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
              >
                Solicitar presupuesto sin compromiso →
              </Link>
              <Link
                to="/#cobertura"
                className="text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
              >
                Ver zona de cobertura en Barcelona →
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <CookieBanner />
      <BackToTop />
    </div>
  );
}